import type {
  IKanbanColumn,
  IKanbanComment,
  IKanbanPerson,
  IKanbanProvider,
  IKanbanTicket,
} from './interfaces.js';

export const kanbanDemoPeople: IKanbanPerson[] = [
  { id: 'mara', name: 'Mara Chen' },
  { id: 'jonas', name: 'Jonas Berg' },
  { id: 'alex', name: 'Alex Rivera' },
];

export const kanbanDemoColumns: IKanbanColumn[] = [
  { id: 'backlog', title: 'Backlog' },
  { id: 'progress', title: 'In progress' },
  { id: 'review', title: 'In review' },
  { id: 'done', title: 'Done', done: true },
];

export const createKanbanDemo = (): { tickets: IKanbanTicket[]; provider: IKanbanProvider } => {
  let tickets: IKanbanTicket[] = [
    {
      id: 'epic-1',
      key: 'CAT-100',
      kind: 'epic',
      title: 'A calmer workspace',
      description: 'Make daily work feel focused, connected and easy to navigate.',
      columnId: 'progress',
      order: 0,
    },
    {
      id: 'epic-2',
      key: 'CAT-200',
      kind: 'epic',
      title: 'Built for teams',
      description: 'Keep everyone close to the work and the decisions behind it.',
      columnId: 'progress',
      order: 1,
    },
    {
      id: 'task-1',
      key: 'CAT-101',
      kind: 'task',
      title: 'Design the project overview',
      description: 'Give each project a clear home. Surface the next action, recent activity and open questions.',
      parentId: 'epic-1',
      columnId: 'backlog',
      order: 0,
      priority: 'normal',
      assignee: kanbanDemoPeople[0],
      labels: ['Design'],
    },
    {
      id: 'task-2',
      key: 'CAT-102',
      kind: 'task',
      title: 'Connect related tickets',
      description: 'Type #_ in a comment to search for another ticket. Keep the reference connected when its title changes.',
      parentId: 'epic-2',
      columnId: 'progress',
      order: 0,
      priority: 'high',
      assignee: kanbanDemoPeople[1],
      labels: ['Collaboration'],
    },
    {
      id: 'task-3',
      key: 'CAT-103',
      kind: 'task',
      title: 'Make every action keyboard accessible',
      description: 'Support focus, selection, navigation and moving tickets without a mouse.',
      parentId: 'epic-1',
      columnId: 'progress',
      order: 1,
      priority: 'normal',
      assignee: kanbanDemoPeople[2],
      labels: ['Accessibility'],
    },
    {
      id: 'task-4',
      key: 'CAT-104',
      kind: 'task',
      title: 'Review the ticket detail experience',
      description: 'Check hierarchy navigation, discussion drafts and small-screen layouts.',
      parentId: 'epic-2',
      columnId: 'review',
      order: 0,
      priority: 'low',
      assignee: kanbanDemoPeople[0],
    },
    {
      id: 'task-5',
      key: 'CAT-105',
      kind: 'task',
      title: 'Unify status and priority styles',
      description: 'Use shared catalog tokens in light and dark themes.',
      parentId: 'epic-1',
      columnId: 'done',
      order: 0,
      assignee: kanbanDemoPeople[2],
      labels: ['Foundations'],
    },
    {
      id: 'sub-1',
      key: 'CAT-106',
      kind: 'subtask',
      title: 'Anchor search to the cursor',
      parentId: 'task-2',
      columnId: 'done',
      order: 0,
      assignee: kanbanDemoPeople[1],
    },
    {
      id: 'sub-2',
      key: 'CAT-107',
      kind: 'subtask',
      title: 'Restore focus after selection',
      parentId: 'task-2',
      columnId: 'progress',
      order: 1,
      assignee: kanbanDemoPeople[1],
    },
    {
      id: 'sub-3',
      key: 'CAT-108',
      kind: 'subtask',
      title: 'Check arrow-key navigation',
      parentId: 'task-3',
      columnId: 'done',
      order: 0,
      assignee: kanbanDemoPeople[2],
    },
  ];
  let comments: IKanbanComment[] = [
    {
      id: 'comment-1',
      ticketId: 'task-2',
      author: kanbanDemoPeople[0],
      createdAt: '2026-09-18T09:30:00Z',
      body: [
        {
          type: 'text',
          text: 'The picker should feel like part of the conversation. Can we check this together with ',
        },
        { type: 'reference', id: 'task-3', label: '#CAT-103 Keyboard accessibility' },
        { type: 'text', text: '?' },
      ],
    },
    {
      id: 'comment-2',
      ticketId: 'task-2',
      author: kanbanDemoPeople[1],
      createdAt: '2026-09-18T09:42:00Z',
      body: [
        {
          type: 'text',
          text: 'Yes. Search receives focus immediately, and choosing a ticket returns you to exactly where you were writing.',
        },
      ],
    },
  ];
  let nextKey = 300;
  // Demo-only in-memory provider. Applications persist through their own authorized APIs.
  const provider: IKanbanProvider = {
    getTicket: async id => {
      const ticket = tickets.find(item => item.id === id);
      if (!ticket) throw new Error('Ticket not found.');
      return ticket;
    },
    searchTickets: async query =>
      tickets.filter(ticket =>
        `${ticket.key} ${ticket.title}`.toLowerCase().includes(query.toLowerCase()),
      ),
    loadComments: async id => comments.filter(comment => comment.ticketId === id),
    addComment: async input => {
      const comment: IKanbanComment = {
        ...input,
        id: crypto.randomUUID(),
        author: kanbanDemoPeople[0],
        createdAt: new Date().toISOString(),
      };
      comments = [...comments, comment];
      return comment;
    },
    updateTicket: async ({ ticketId, changes }) => {
      const previous = tickets.find(ticket => ticket.id === ticketId)!;
      const ticket: IKanbanTicket = {
        ...previous,
        ...changes,
        assignee: changes.assignee === null ? undefined : changes.assignee ?? previous.assignee,
        parentId: changes.parentId === null ? undefined : changes.parentId ?? previous.parentId,
      };
      tickets = tickets.map(item => item.id === ticketId ? ticket : item);
      return ticket;
    },
    moveTicket: async ({ ticketId, columnId, beforeTicketId }) => {
      const ticket = tickets.find(item => item.id === ticketId)!;
      const destination = tickets
        .filter(
          item => item.id !== ticketId && item.columnId === columnId && item.kind === ticket.kind,
        )
        .sort((a, b) => a.order - b.order);
      const index = beforeTicketId
        ? destination.findIndex(item => item.id === beforeTicketId)
        : destination.length;
      destination.splice(index < 0 ? destination.length : index, 0, { ...ticket, columnId });
      const changed = destination.map((item, order) => ({ ...item, order }));
      tickets = tickets.map(item => changed.find(record => record.id === item.id) ?? item);
      return changed;
    },
    createTicket: async input => {
      const ticket: IKanbanTicket = {
        ...input,
        id: crypto.randomUUID(),
        key: `CAT-${nextKey++}`,
        order: tickets.length,
      };
      tickets = [...tickets, ticket];
      return ticket;
    },
  };
  return { tickets, provider };
};
